import { useId } from "react";

import { LoadingNote } from "../lib/Loading.tsx";
import type { Choice } from "../lib/choices.ts";
import { SOURCE_KINDS, VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import type { SourceKind } from "./viewingRooms.constants.ts";
import { WorkPickerDialog } from "./WorkPickerDialog.tsx";
import { WORK_PICKER_COPY } from "./workPicker.constants.ts";
import { isChosen } from "./viewingRooms.model.ts";
import { RoomWorksPreview } from "./RoomWorksPreview.tsx";
import type { PreviewView, RoomDraft, SourceView } from "./viewingRooms.model.ts";

/**
 * What goes in the room.
 *
 * Checkbox chips rather than a select, because this picks many and a select
 * picks one — the same reason `CollectionHead` is a switcher and this is not.
 */
export function RoomSourcePicker({
  source,
  chosenCount,
  onKind,
  onToggle,
  onOpenPicker,
}: {
  source: SourceView;
  chosenCount: number;
  onKind: (kind: SourceKind) => void;
  onToggle: (choice: Choice) => void;
  onOpenPicker: () => void;
}) {
  return (
    <fieldset className="rooms__fieldset">
      <legend className="rooms__legend">{VIEWING_ROOMS_COPY.sourceLegend}</legend>

      <div className="rooms__choices">
        {SOURCE_KINDS.map((kind) => (
          <button
            key={kind.key}
            type="button"
            className={`rooms__choice${source.kind === kind.key ? " rooms__choice--on" : ""}`}
            aria-pressed={source.kind === kind.key}
            onClick={() => onKind(kind.key)}
          >
            {kind.label}
          </button>
        ))}
      </div>

      <p className="rooms__hint">{VIEWING_ROOMS_COPY.sourceLiveNote}</p>

      {source.loading && <LoadingNote message={VIEWING_ROOMS_COPY.sourceLoading} />}
      {source.nothing && <p className="rooms__note">{source.nothing}</p>}

      {/* Works get a button rather than a list. Chips are right for the handful
          of collections a person owns and wrong for the hundreds of works
          inside them — which is what the dialog exists to browse. */}
      {source.kind === "works" && source.nothing === null && (
        <div className="rooms__picker-trigger">
          <button type="button" className="rooms__action" onClick={onOpenPicker}>
            {WORK_PICKER_COPY.open}
          </button>
          <span className="rooms__hint">
            {chosenCount === 0
              ? WORK_PICKER_COPY.chosenNone
              : `${String(chosenCount)} chosen`}
          </span>
        </div>
      )}

      {source.options.length > 0 && (
        <>
          <div className="rooms__choices rooms__choices--wrap">
            {source.options.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`rooms__choice${
                  isChosen(source.chosen, option.key) ? " rooms__choice--on" : ""
                }`}
                aria-pressed={isChosen(source.chosen, option.key)}
                onClick={() => onToggle(option)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="rooms__hint">{VIEWING_ROOMS_COPY.sourceNote}</p>
        </>
      )}
    </fieldset>
  );
}

export function RoomCreateForm({
  heading,
  submitLabel,
  busyLabel,
  liveNote,
  editing,
  preview,
  draft,
  source,
  problem,
  error,
  busy,
  picking,
  onEdit,
  onKind,
  onToggle,
  onOpenPicker,
  onClosePicker,
  onSubmit,
  onCancel,
}: {
  heading: string;
  submitLabel: string;
  busyLabel: string;
  /** Only set when editing: the link is already out there. */
  liveNote: string | null;
  editing: boolean;
  /** What the room holds today. Null when creating — there is no room yet. */
  preview: PreviewView | null;
  draft: RoomDraft;
  source: SourceView;
  problem: string | null;
  /** The server's refusal, which fails differently from a rule this form knows. */
  error: string | null;
  busy: boolean;
  picking: boolean;
  onEdit: (patch: Partial<RoomDraft>) => void;
  onKind: (kind: SourceKind) => void;
  onToggle: (choice: Choice) => void;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const subtitleId = useId();
  const messageId = useId();
  const passwordId = useId();

  return (
    // A fragment, and the dialog is a SIBLING of the form rather than a child.
    //
    // <dialog> renders in the top layer, so where it sits in the DOM makes no
    // difference to how it looks — but an input inside the form is owned by
    // that form whatever the layout does, and Enter in the picker's search box
    // would submit the room. There is a test that presses Enter, so that this
    // cannot be quietly undone by a later tidy-up.
    <>
      <form
        className="rooms__form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <h2 className="rooms__lead">{heading}</h2>

        {liveNote && <p className="rooms__withheld">{liveNote}</p>}

        <div className="rooms__field">
          <label className="rooms__label" htmlFor={titleId}>
            {VIEWING_ROOMS_COPY.titleLabel}
          </label>
          <input
            id={titleId}
            className="rooms__input"
            value={draft.title}
            placeholder={VIEWING_ROOMS_COPY.titlePlaceholder}
            onChange={(event) => onEdit({ title: event.target.value })}
          />
        </div>

        <div className="rooms__field">
          <label className="rooms__label" htmlFor={subtitleId}>
            {VIEWING_ROOMS_COPY.subtitleLabel}
          </label>
          <input
            id={subtitleId}
            className="rooms__input"
            value={draft.subtitle}
            placeholder={VIEWING_ROOMS_COPY.subtitlePlaceholder}
            onChange={(event) => onEdit({ subtitle: event.target.value })}
          />
        </div>

        <div className="rooms__field">
          <label className="rooms__label" htmlFor={messageId}>
            {VIEWING_ROOMS_COPY.messageLabel}
          </label>
          <textarea
            id={messageId}
            className="rooms__input"
            rows={3}
            value={draft.message}
            onChange={(event) => onEdit({ message: event.target.value })}
          />
        </div>

        <RoomSourcePicker
          source={source}
          chosenCount={draft.chosen.length}
          onKind={onKind}
          onToggle={onToggle}
          onOpenPicker={onOpenPicker}
        />

        {editing && (
          <RoomWorksPreview preview={preview} note={VIEWING_ROOMS_COPY.previewLiveNote} />
        )}

        <fieldset className="rooms__fieldset">
          <legend className="rooms__legend">{VIEWING_ROOMS_COPY.showsLegend}</legend>

          <label className="rooms__toggle">
            <input
              type="checkbox"
              checked={draft.showProvenance}
              onChange={(event) =>
                onEdit({
                  showProvenance: event.target.checked,
                  // The present owner is a facet of the chain: switching the chain
                  // off must not leave a tick behind that says otherwise.
                  showPresentOwner: event.target.checked && draft.showPresentOwner,
                })
              }
            />
            <span>
              {VIEWING_ROOMS_COPY.showProvenance}
              <em className="rooms__hint">{VIEWING_ROOMS_COPY.showProvenanceHint}</em>
            </span>
          </label>

          {draft.showProvenance && (
            <label className="rooms__toggle rooms__toggle--nested">
              <input
                type="checkbox"
                checked={draft.showPresentOwner}
                onChange={(event) => onEdit({ showPresentOwner: event.target.checked })}
              />
              <span>
                {VIEWING_ROOMS_COPY.showPresentOwner}
                <em className="rooms__hint">{VIEWING_ROOMS_COPY.showPresentOwnerHint}</em>
              </span>
            </label>
          )}

          <label className="rooms__toggle">
            <input
              type="checkbox"
              checked={draft.showDimensions}
              onChange={(event) => onEdit({ showDimensions: event.target.checked })}
            />
            <span>
              {VIEWING_ROOMS_COPY.showDimensions}
              <em className="rooms__hint">{VIEWING_ROOMS_COPY.showDimensionsHint}</em>
            </span>
          </label>

          {/* Not a disabled toggle, because it is not a choice. */}
          <div className="rooms__withheld">
            <p className="rooms__withheld-title">{VIEWING_ROOMS_COPY.withheldTitle}</p>
            <p>{VIEWING_ROOMS_COPY.withheldBody}</p>
          </div>
        </fieldset>

        <div className="rooms__field">
          <label className="rooms__label" htmlFor={passwordId}>
            {editing
              ? VIEWING_ROOMS_COPY.editPasswordLabel
              : VIEWING_ROOMS_COPY.passwordLabel}
          </label>
          <input
            id={passwordId}
            className="rooms__input"
            type="password"
            autoComplete="new-password"
            value={draft.password}
            disabled={draft.clearPassword}
            onChange={(event) => onEdit({ password: event.target.value })}
          />
          <p className="rooms__hint">
            {editing
              ? VIEWING_ROOMS_COPY.editPasswordHint
              : VIEWING_ROOMS_COPY.passwordHint}
          </p>

          {/* Removing a password is a deliberate act, not the absence of one:
              the field above is empty every time this form opens, so "empty
              means no password" would unlock a room on a rename. */}
          {editing && (
            <label className="rooms__toggle">
              <input
                type="checkbox"
                checked={draft.clearPassword}
                onChange={(event) =>
                  onEdit({ clearPassword: event.target.checked, password: "" })
                }
              />
              <span>{VIEWING_ROOMS_COPY.editPasswordClear}</span>
            </label>
          )}
        </div>

        <p className="rooms__note">{VIEWING_ROOMS_COPY.emailNote}</p>

        {problem && (
          <p className="rooms__error" role="alert">
            {problem}
          </p>
        )}

        {error && (
          <p className="rooms__error" role="alert">
            {error}
          </p>
        )}

        <div className="rooms__row-actions">
          <button type="submit" className="rooms__submit" disabled={busy}>
            {busy ? busyLabel : submitLabel}
          </button>
          <button type="button" className="rooms__action" onClick={onCancel}>
            {VIEWING_ROOMS_COPY.cancel}
          </button>
          </div>
      </form>

      <WorkPickerDialog
        open={picking}
        selected={draft.chosen}
        onToggle={onToggle}
        onClose={onClosePicker}
      />
    </>
  );
}
